/**
 * Where the theatre map's nodes go (08 §2.2, 04 §2.2): columns left to right, each column's
 * rows centred vertically, links as straight segments between node centres. Pure: no DOM, so
 * the geometry is tested in node and the screen only has to draw it.
 */

import type { Operation, TheatreNode } from '../../../meta/types';

export interface GraphPoint {
  x: number;
  y: number;
}

export interface GraphEdge {
  from: string;
  to: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

export interface GraphLayout {
  /** Node id -> centre, in whole pixels inside the `width` x `height` box. */
  points: Map<string, GraphPoint>;
  edges: GraphEdge[];
  /** How many distinct columns the nodes occupy. */
  columns: number;
}

/**
 * Lay `nodes` out in a `width` x `height` box with `pad` kept clear on every side. Columns are
 * spaced evenly by their `column` order (one column sits in the middle); every column uses the
 * same row pitch, taken from the tallest column, and is centred on the box's mid-line. Links to
 * nodes outside `nodes` (another theatre) produce no edge.
 */
export function layoutGraph(nodes: readonly TheatreNode[], width: number, height: number, pad = 16): GraphLayout {
  const columnIds = [...new Set(nodes.map((n) => n.column))].sort((a, b) => a - b);
  const byColumn = columnIds.map((c) => nodes.filter((n) => n.column === c).sort((a, b) => a.row - b.row));
  const tallest = byColumn.reduce((max, col) => Math.max(max, col.length), 1);
  const innerW = Math.max(0, width - pad * 2);
  const innerH = Math.max(0, height - pad * 2);
  const pitch = innerH / tallest;

  const points = new Map<string, GraphPoint>();
  byColumn.forEach((col, i) => {
    const x = columnIds.length <= 1 ? width / 2 : pad + (innerW * i) / (columnIds.length - 1);
    col.forEach((node, j) => {
      const y = height / 2 + (j - (col.length - 1) / 2) * pitch;
      points.set(node.id, { x: Math.round(x), y: Math.round(y) });
    });
  });

  const edges: GraphEdge[] = [];
  for (const node of nodes) {
    const a = points.get(node.id);
    if (a === undefined) continue;
    for (const to of node.links) {
      const b = points.get(to);
      if (b !== undefined) edges.push({ from: node.id, to, x1: a.x, y1: a.y, x2: b.x, y2: b.y });
    }
  }
  return { points, edges, columns: columnIds.length };
}

/** The theatre on show: where the open nodes are, else where the company stands, else the first. */
export function currentTheatre(op: Operation, openNodes: readonly TheatreNode[]): number {
  const open = openNodes[0];
  if (open !== undefined) return open.theatre;
  const here = op.nodes.find((n) => n.id === op.position);
  return here?.theatre ?? 0;
}

export function theatreNodes(op: Operation, theatre: number): TheatreNode[] {
  return op.nodes.filter((n) => n.theatre === theatre);
}

/** The column the player is choosing in now (the open nodes' column), or -1 when nothing is open. */
export function currentColumn(openNodes: readonly TheatreNode[], theatre: number): number {
  const columns = openNodes.filter((n) => n.theatre === theatre).map((n) => n.column);
  return columns.length === 0 ? -1 : Math.min(...columns);
}

function visited(node: TheatreNode): boolean {
  return node.state === 'done' || node.state === 'failed';
}

/**
 * Is this link part of the inked path? Yes between two nodes the company has been through, and
 * from the node it stands on to the nodes open to it now.
 */
export function isChosenEdge(from: TheatreNode, to: TheatreNode, position: string | null): boolean {
  if (!visited(from)) return false;
  return visited(to) || (to.state === 'open' && from.id === position);
}
